/**
 * i18n string set for `@metro/suggester`.
 *
 * Single English set today — metro-frontend has no runtime i18n
 * framework yet (mirrors `route-card`, whose labels are inline
 * English). The strings are funnelled through one record so a future
 * locale layer can swap the object without touching component code,
 * and so the keys stay a stable cross-platform contract (the iOS
 * `Localizable.xcstrings` carries the same logical keys).
 *
 * Copy is verbatim from the TASK-318 Figma "Suggester — States" frames.
 */
export interface SuggesterStrings {
  /** Search field placeholder. */
  readonly searchPlaceholder: string
  /** Accessible label of the close control. */
  readonly close: string
  /** Accessible label of the search-field clear control. */
  readonly clearSearch: string
  /** Accessible label of the alert dim-backdrop dismiss control. */
  readonly dismiss: string
  /** Location banner — primary line ("Find the nearest station"). */
  readonly locationBannerTitle: string
  /** Location banner — CTA pill label ("Use location"). */
  readonly locationBannerCta: string
  /** Nearby section header. */
  readonly nearby: string
  /** Favorites section header. */
  readonly favorites: string
  /** Recents section header. */
  readonly recents: string
  /** Popular section header. */
  readonly popular: string
  /** Offline notice text. */
  readonly offlineNotice: string
  /** "Station closed" badge label on a Closed row. */
  readonly stationClosed: string
  /** "Service alert" badge label on an Advisory row. */
  readonly serviceAlert: string
  /** Empty-state title. */
  readonly emptyTitle: string
  /** Empty-state subtitle. */
  readonly emptySubtitle: string
  /** "Location Access Required" alert — title. */
  readonly deniedAlertTitle: string
  /** "Location Access Required" alert — body. */
  readonly deniedAlertBody: string
  /** "Location Access Required" alert — cancel action. */
  readonly deniedCancel: string
  /** "Location Access Required" alert — open-settings action. */
  readonly deniedOpenSettings: string
  /** Disruption Details — header title. */
  readonly disruptionTitle: string
  /** Disruption Details — accessible label of the back control. */
  readonly disruptionBack: string
  /** Disruption Details — "Type" meta label. */
  readonly disruptionType: string
  /** Disruption Details — "Active" meta label. */
  readonly disruptionActive: string
  /** Disruption Details — "Affected lines" meta label. */
  readonly disruptionAffectedLines: string
  /** Disruption Details — "What's happening" body heading. */
  readonly disruptionWhatsHappening: string
  /** Disruption Details — TfL source line. */
  readonly disruptionSource: string
  /** Disruption Details — error-state title. */
  readonly disruptionErrorTitle: string
  /** Disruption Details — error-state subtitle. */
  readonly disruptionErrorSubtitle: string
  /** Disruption Details — error-state retry action. */
  readonly disruptionRetry: string
}

/**
 * Default (English) string set. UK voice for the primary `en-GB`
 * audience. App name is "London Metro".
 */
export const defaultStrings: SuggesterStrings = {
  searchPlaceholder: 'Search station',
  close: 'Close',
  clearSearch: 'Clear search',
  dismiss: 'Dismiss',
  locationBannerTitle: 'Find the nearest station',
  locationBannerCta: 'Use location',
  nearby: 'Nearby',
  favorites: 'Favorites',
  recents: 'Recent',
  popular: 'Popular',
  offlineNotice: 'No connection — showing offline results',
  stationClosed: 'Station closed',
  serviceAlert: 'Service alert',
  emptyTitle: 'No stations found',
  emptySubtitle: 'Try a different name or check the spelling.',
  deniedAlertTitle: 'Location Access Required',
  deniedAlertBody:
    'Please enable location access to find nearby metro stations and receive accurate route guidance.',
  deniedCancel: 'Cancel',
  deniedOpenSettings: 'Open Settings',
  disruptionTitle: 'Station details',
  disruptionBack: 'Back',
  disruptionType: 'Type',
  disruptionActive: 'Active',
  disruptionAffectedLines: 'Affected lines',
  disruptionWhatsHappening: "What's happening",
  disruptionSource: 'Source: TfL · Updated just now',
  disruptionErrorTitle: "We couldn't load the latest info",
  disruptionErrorSubtitle: 'Please take care on your journey.',
  disruptionRetry: 'Retry',
}
